import { MedelyDateTime } from '@medely/date-time-tools';
import { gateNames } from '@mp/constants';
import { FragmentType, parseFragmentData } from '@mp/graphql/generated';
import { useCurrentUser, useGate } from '@mp/hooks';
import { JobDetailsFragment } from '../jobDetailsComponents/graphql';

const isDecidedWithinHours = (
  decidedAt: string | null | undefined,
  timezone: string,
  hoursThreshold: number,
): boolean => {
  if (!decidedAt) {
    return false;
  }

  const decidedDateTime = new MedelyDateTime(decidedAt, { tz: timezone });
  const now = MedelyDateTime.now({ tz: timezone });

  return decidedDateTime.difference(now, 'hours') <= hoursThreshold;
};

export const useShiftSwapData = ({
  job: inputJob,
}: {
  job?: FragmentType<typeof JobDetailsFragment> | null;
}) => {
  const job = parseFragmentData(JobDetailsFragment, inputJob);

  const { currentUser, isSaas } = useCurrentUser();
  const isShiftSwapEnabled = useGate(gateNames.direct_shift_release_pro);

  const requesteeId = job?.pending_shift_request?.requestee_account_info?.professional_id;

  const proIsRequestee = requesteeId === currentUser?.professional?.id;

  const timezone = job?.location?.timezone_lookup || 'system';

  const proIsRequester =
    job?.pending_shift_request?.requester_account_info?.professional_id ===
    currentUser?.professional?.id;

  const pendingRequestStatus = job?.pending_shift_request?.status;
  const deniedRequestStatus = job?.latest_declined_shift_request?.status;

  const hasPendingApprovedRequest =
    job?.latest_approved_shift_request?.status === 'approved' ||
    job?.latest_approved_shift_request?.request_kind === 'release';

  const pendingRequestType = job?.pending_shift_request?.request_kind;
  const deniedRequestType = job?.latest_declined_shift_request?.request_kind;

  const canShowShiftReleaseInfo = isShiftSwapEnabled && isSaas;

  const requestIsSwap = pendingRequestType === 'swap';
  const requestIsRelease = pendingRequestType === 'release';
  const requestIsDrop = pendingRequestType === 'drop';

  const proHasSwapRequestToAcceptOrDecline =
    canShowShiftReleaseInfo &&
    proIsRequestee &&
    pendingRequestStatus === 'pending_requestee' &&
    requestIsSwap;

  const proHasSwapRequestPendingFacility =
    canShowShiftReleaseInfo &&
    proIsRequestee &&
    pendingRequestStatus === 'pending' &&
    requestIsSwap;

  const proDidSwapRequest =
    canShowShiftReleaseInfo &&
    proIsRequester &&
    (pendingRequestStatus === 'pending_requestee' || pendingRequestStatus === 'pending') &&
    requestIsSwap;

  const proAwaitingReleaseRequest =
    canShowShiftReleaseInfo &&
    proIsRequester &&
    pendingRequestStatus === 'pending' &&
    requestIsRelease;

  const proAwaitingDropRequest =
    canShowShiftReleaseInfo &&
    proIsRequester &&
    pendingRequestStatus === 'pending' &&
    requestIsDrop;

  const swapBannerName = `${job?.pending_shift_request?.requester_account_info?.name || ''}`;

  const proHasShiftRequestPending = Boolean(job?.pending_shift_request);

  const declinedDecidedAt = job?.latest_declined_shift_request?.decided_at;
  const isDeclinedWithin12Hours = isDecidedWithinHours(declinedDecidedAt, timezone, 12);

  const requestRejected =
    canShowShiftReleaseInfo && deniedRequestStatus === 'declined' && isDeclinedWithin12Hours;
  const requestExpired =
    canShowShiftReleaseInfo && deniedRequestStatus === 'auto_declined' && isDeclinedWithin12Hours;

  const hasPendingOrApprovedRequest = proHasShiftRequestPending || hasPendingApprovedRequest;

  return {
    proHasSwapRequestToAcceptOrDecline,
    proHasSwapRequestPendingFacility,
    proDidSwapRequest,
    proAwaitingReleaseRequest,
    proAwaitingDropRequest,
    requestRejected: hasPendingOrApprovedRequest ? false : requestRejected,
    requestExpired: hasPendingOrApprovedRequest ? false : requestExpired,
    swapBannerName,
    requestIsDrop,
    requestType: proHasShiftRequestPending ? pendingRequestType : deniedRequestType,
    canShowShiftReleaseInfo,
    requesteeId,
    proIsRequestee,
    proAskedForReleaseButJobIsNotReassigned: hasPendingApprovedRequest,
    expirationInfo: hasPendingApprovedRequest
      ? job?.latest_approved_shift_request?.expires_at
      : job?.pending_shift_request?.expires_at,
    timezone,
    reasonMessage: job?.latest_declined_shift_request?.reason,
  };
};
